import React, { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, Star, Tag } from "@/lib/icons";
import { toast } from "sonner";

const MAX_CATEGORIES = 10;

export function selectedCategories(member) {
  const values = Array.isArray(member?.categories) ? member.categories.filter(Boolean) : [];
  if (member?.category && !values.some((value) => value.toLowerCase() === member.category.toLowerCase())) {
    values.unshift(member.category);
  }
  return Array.from(new Map(values.map((value) => [value.toLowerCase(), value])).values()).slice(0, MAX_CATEGORIES);
}

export function CategoryChips({ categories = [], primary, compact = false, className = "" }) {
  if (!categories.length) return <span className="text-sm italic text-white/40">No categories selected</span>;
  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {categories.map((name) => {
        const isPrimary = name === primary;
        return (
          <span key={name} className={`inline-flex items-center gap-1.5 rounded-full border font-semibold ${compact ? "px-2.5 py-1 text-[10px]" : "px-3 py-1.5 text-xs"} ${
            isPrimary
              ? "member-category-primary border-lime-300/45 bg-lime-300/10 text-lime-200"
              : "member-category-secondary border-violet-400/30 bg-violet-500/10 text-violet-200"
          }`}>
            {isPrimary ? <Star size={compact ? 10 : 12} /> : <Tag size={compact ? 10 : 12} />}
            {name}
            {isPrimary && <span className="opacity-65">Primary</span>}
          </span>
        );
      })}
    </div>
  );
}

export default function CategoryMultiSelect({ options = [], categories = [], primary, onChange, testid = "categories" }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const selected = categories.filter(Boolean);

  useEffect(() => {
    const close = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const emit = (next, nextPrimary = primary) => {
    const validPrimary = next.includes(nextPrimary) ? nextPrimary : (next[0] || "");
    onChange?.({ categories: next, category: validPrimary });
  };

  const toggle = (name) => {
    if (selected.includes(name)) {
      emit(selected.filter((value) => value !== name));
      return;
    }
    if (selected.length >= MAX_CATEGORIES) {
      toast.info("You can select up to 10 categories.");
      return;
    }
    emit([...selected, name], primary || name);
  };

  return (
    <div ref={rootRef} className="relative" data-testid={testid}>
      <button type="button" onClick={() => setOpen((value) => !value)}
        data-testid={`${testid}-trigger`}
        className="flex min-h-11 w-full items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-left text-sm text-white transition hover:border-violet-400/40">
        <span>{selected.length ? `${selected.length} categor${selected.length === 1 ? "y" : "ies"} selected` : "Select multiple categories"}</span>
        <span className="flex items-center gap-2 text-xs text-white/45">
          {selected.length}/{MAX_CATEGORIES}<ChevronDown size={15} className={open ? "rotate-180" : ""} />
        </span>
      </button>

      {open && (
        <div className="absolute left-0 right-0 z-50 mt-2 max-h-72 overflow-y-auto rounded-2xl border border-white/10 bg-[#171720] p-2 shadow-2xl">
          {options.map((option) => {
            const name = typeof option === "string" ? option : option.name;
            const checked = selected.includes(name);
            return (
              <div key={name} className="flex items-center gap-2 rounded-xl px-2 py-1.5 hover:bg-white/5">
                <button type="button" onClick={() => toggle(name)} data-testid={`${testid}-option-${name}`}
                  className="flex min-w-0 flex-1 items-center gap-3 text-left text-sm text-white/80">
                  <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${checked ? "border-violet-400 bg-violet-500/30" : "border-white/20"}`}>
                    {checked && <Check size={13} className="text-violet-100" />}
                  </span>
                  <span className="truncate">{name}</span>
                </button>
                {checked && (
                  <button type="button" onClick={() => emit(selected, name)} title={`Make ${name} primary`}
                    data-testid={`${testid}-primary-${name}`}
                    className={`rounded-lg p-2 transition ${name === primary ? "bg-lime-300/10 text-lime-200" : "text-white/30 hover:bg-white/5 hover:text-lime-200"}`}>
                    <Star size={14} />
                  </button>
                )}
              </div>
            );
          })}
          {!options.length && <div className="px-3 py-6 text-center text-xs text-white/40">No categories are available.</div>}
          <div className="sticky bottom-0 mt-2 border-t border-white/10 bg-[#171720] px-3 py-2 text-[10px] leading-4 text-white/45">
            Select up to 10. Use the star to choose the primary category.
          </div>
        </div>
      )}

      <CategoryChips categories={selected} primary={primary} className="mt-3" />
    </div>
  );
}
